import React, { useState } from "react";
import {
  Mail,
  Phone,
  Building2,
  Send,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Calendar,
  ArrowUpRight,
} from "lucide-react";
import { submitLead } from "../lib/leads";

interface ContactSectionProps {
  selectedTopic: string;
  onTopicChange: (topic: string) => void;
}

export const ContactSection: React.FC<ContactSectionProps> = ({
  selectedTopic,
  onTopicChange,
}) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [company, setCompany] = useState("");
  const [teamSize, setTeamSize] = useState<"<20" | "20-50" | "50-200" | "200+">(
    "20-50",
  );
  const [message, setMessage] = useState("");

  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const calendarBookingUrl =
    import.meta.env.VITE_CALENDAR_BOOKING_URL ||
    `mailto:info@innovriddhi.com?subject=${encodeURIComponent(
      `Operational Walkthrough Request — ${company || "Enterprise"}`,
    )}&body=${encodeURIComponent(
      `Hi Innovriddhi Team,\n\nI would like to schedule a 20-minute operational walkthrough for ${company || "our company"} (${teamSize} reps).\n\nBest regards,\n${name}\n${phone}`,
    )}`;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);

    // Basic Indian phone validation
    const cleanPhone = phone.replace(/[\s-]/g, "");
    const phoneRegex = /^(?:\+?91|0)?[6-9]\d{9}$/;
    if (!phoneRegex.test(cleanPhone)) {
      setErrorMessage(
        "Please enter a valid 10-digit Indian mobile number (+91).",
      );
      return;
    }

    setLoading(true);

    const res = await submitLead({
      name,
      email,
      phone: cleanPhone,
      company,
      topic: selectedTopic,
      teamSize,
      message,
    });

    setLoading(false);

    if (res.ok) {
      setSubmitted(true);
    } else {
      setErrorMessage(
        res.error ||
          "Failed to submit inquiry. Please email info@innovriddhi.com.",
      );
    }
  };

  const handleReset = () => {
    setSubmitted(false);
    setName("");
    setEmail("");
    setPhone("");
    setCompany("");
    setTeamSize("20-50");
    setMessage("");
    setErrorMessage(null);
  };

  return (
    <section
      id="contact"
      className="py-20 lg:py-28 bg-canvas border-b border-charcoal/5"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="text-center space-y-4 mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cream-300 text-xs font-mono font-medium text-charcoal-700 uppercase tracking-widest">
              Enterprise Inquiries
            </div>
            <h2 className="font-display text-3xl sm:text-4xl font-extrabold text-ink tracking-tight">
              Partner with Innovriddhi
            </h2>
            <p className="text-base text-ink-secondary leading-relaxed max-w-xl mx-auto">
              Discuss enterprise deployments for{" "}
              <strong className="text-ink">ivManage</strong>, request early
              access for our upcoming platform (
              <strong className="text-ink">Product 02 · Name TBA</strong>), or
              explore corporate partnerships.
            </p>
          </div>

          {/* Form Container */}
          <div className="rounded-3xl bg-white p-8 sm:p-12 border border-charcoal/10 shadow-sm relative overflow-hidden">
            {submitted ? (
              <div className="py-4 text-center space-y-6">
                <div className="w-16 h-16 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center mx-auto border border-emerald-200">
                  <CheckCircle2 className="w-8 h-8" />
                </div>
                <div className="space-y-2">
                  <h3 className="font-display text-2xl font-bold text-ink">
                    Inquiry Received & Queued
                  </h3>
                  <p className="text-sm text-ink-secondary max-w-md mx-auto leading-relaxed">
                    Thank you, <strong className="text-ink">{name}</strong>. An
                    enterprise specialist will review your requirements for{" "}
                    <strong className="text-ink">{company}</strong> ({teamSize}{" "}
                    reps) and contact you at{" "}
                    <strong className="text-ink">{email}</strong> within one
                    business day.
                  </p>
                </div>

                {/* Instant Scheduling Callout */}
                <div className="mt-8 p-6 rounded-2xl bg-cream-100/80 border border-plum/15 text-left space-y-4">
                  <div className="space-y-1.5">
                    <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-plum/10 text-plum font-mono text-[11px] font-semibold uppercase">
                      <Calendar className="w-3.5 h-3.5" />
                      Instant Fast-Track
                    </div>
                    <h4 className="font-display text-base font-bold text-ink">
                      Prefer to speak immediately? Book a 20-Minute Operational
                      Walkthrough
                    </h4>
                    <p className="text-xs text-ink-secondary leading-relaxed">
                      Skip the email delay. Connect directly with our solutions
                      engineering team for an architecture and live platform
                      review tailored to your distribution network.
                    </p>
                  </div>

                  <div className="flex flex-col sm:flex-row items-center gap-3 pt-2">
                    <a
                      href={calendarBookingUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-2.5 rounded-full text-xs font-semibold text-white bg-plum hover:bg-plum-hover shadow-sm transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-plum"
                    >
                      Schedule Operational Walkthrough
                      <ArrowUpRight className="w-4 h-4" />
                    </a>

                    <a
                      href="https://ivmanage.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full text-xs font-semibold text-ink bg-white border border-charcoal/15 hover:border-charcoal/30 shadow-sm transition-colors"
                    >
                      Explore ivManage Live
                      <ArrowUpRight className="w-4 h-4" />
                    </a>
                  </div>
                </div>

                <div className="pt-4">
                  <button
                    type="button"
                    onClick={handleReset}
                    className="inline-flex items-center justify-center px-6 py-2.5 rounded-full text-xs font-mono font-semibold text-plum bg-cream-200 hover:bg-cream-300 transition-colors"
                  >
                    Submit Another Inquiry
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                {errorMessage && (
                  <div className="p-4 rounded-xl bg-red-50 border border-red-200 flex items-start gap-3 text-red-800 text-sm">
                    <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
                    <div className="space-y-1">
                      <p>{errorMessage}</p>
                      <p className="text-xs text-red-600">
                        You can also write directly to{" "}
                        <a
                          href="mailto:info@innovriddhi.com"
                          className="underline font-semibold"
                        >
                          info@innovriddhi.com
                        </a>
                        .
                      </p>
                    </div>
                  </div>
                )}

                {/* Inquiry Topic Selector */}
                <div className="space-y-2">
                  <label
                    htmlFor="inquiry-topic"
                    className="block text-xs font-mono font-semibold uppercase tracking-wider text-ink-secondary"
                  >
                    Inquiry Topic
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <button
                      type="button"
                      onClick={() => onTopicChange("general")}
                      className={`p-3 rounded-xl border text-xs font-semibold text-left transition-all ${
                        selectedTopic === "general"
                          ? "border-plum bg-plum/5 text-plum ring-1 ring-plum"
                          : "border-charcoal/15 bg-white text-ink-secondary hover:border-charcoal/30"
                      }`}
                    >
                      General Corporate
                    </button>
                    <button
                      type="button"
                      onClick={() => onTopicChange("ivmanage")}
                      className={`p-3 rounded-xl border text-xs font-semibold text-left transition-all ${
                        selectedTopic === "ivmanage"
                          ? "border-plum bg-plum/5 text-plum ring-1 ring-plum"
                          : "border-charcoal/15 bg-white text-ink-secondary hover:border-charcoal/30"
                      }`}
                    >
                      ivManage Platform Demo
                    </button>
                    <button
                      type="button"
                      onClick={() => onTopicChange("zentrak")}
                      className={`p-3 rounded-xl border text-xs font-semibold text-left transition-all ${
                        selectedTopic === "zentrak"
                          ? "border-pine bg-pine/5 text-pine ring-1 ring-pine"
                          : "border-charcoal/15 bg-white text-ink-secondary hover:border-charcoal/30"
                      }`}
                    >
                      Product 02 (Name TBA)
                    </button>
                  </div>
                </div>

                {/* Field Force / Fleet Size */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="block text-xs font-mono font-semibold uppercase tracking-wider text-ink-secondary">
                      Field Force / Fleet Size *
                    </label>
                    <span className="text-[11px] font-mono text-ink-muted">
                      Active reps in distribution
                    </span>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {[
                      { id: "<20", label: "< 20 Reps", desc: "Emerging" },
                      { id: "20-50", label: "20–50 Reps", desc: "Mid-Market" },
                      { id: "50-200", label: "50–200 Reps", desc: "Regional" },
                      { id: "200+", label: "200+ Reps", desc: "Enterprise" },
                    ].map((tier) => (
                      <button
                        key={tier.id}
                        type="button"
                        onClick={() => setTeamSize(tier.id as any)}
                        className={`p-3 rounded-xl border text-left transition-all flex flex-col justify-between ${
                          teamSize === tier.id
                            ? selectedTopic === "zentrak"
                              ? "border-pine bg-pine/5 text-pine ring-1 ring-pine"
                              : "border-plum bg-plum/5 text-plum ring-1 ring-plum"
                            : "border-charcoal/15 bg-white text-ink-secondary hover:border-charcoal/30"
                        }`}
                      >
                        <span className="text-xs font-bold text-ink">
                          {tier.label}
                        </span>
                        <span className="text-[10px] font-mono text-ink-muted mt-0.5">
                          {tier.desc}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Two Column Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {/* Name */}
                  <div className="space-y-1.5">
                    <label
                      htmlFor="full-name"
                      className="block text-xs font-mono font-semibold uppercase tracking-wider text-ink-secondary"
                    >
                      Full Name *
                    </label>
                    <input
                      id="full-name"
                      type="text"
                      required
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="e.g. Yash Rajpurohit"
                      className="w-full px-4 py-2.5 rounded-xl border border-charcoal/20 bg-white text-sm text-ink placeholder:text-ink-muted focus:outline-none focus:ring-2 focus:ring-plum focus:border-plum"
                    />
                  </div>

                  {/* Work Email */}
                  <div className="space-y-1.5">
                    <label
                      htmlFor="work-email"
                      className="block text-xs font-mono font-semibold uppercase tracking-wider text-ink-secondary"
                    >
                      Work Email *
                    </label>
                    <input
                      id="work-email"
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="name@company.com"
                      className="w-full px-4 py-2.5 rounded-xl border border-charcoal/20 bg-white text-sm text-ink placeholder:text-ink-muted focus:outline-none focus:ring-2 focus:ring-plum focus:border-plum"
                    />
                  </div>

                  {/* Phone */}
                  <div className="space-y-1.5">
                    <label
                      htmlFor="mobile-phone"
                      className="block text-xs font-mono font-semibold uppercase tracking-wider text-ink-secondary"
                    >
                      Phone Number (+91) *
                    </label>
                    <input
                      id="mobile-phone"
                      type="tel"
                      required
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder="+91 98765 43210"
                      className="w-full px-4 py-2.5 rounded-xl border border-charcoal/20 bg-white text-sm text-ink placeholder:text-ink-muted focus:outline-none focus:ring-2 focus:ring-plum focus:border-plum font-mono"
                    />
                  </div>

                  {/* Company Name */}
                  <div className="space-y-1.5">
                    <label
                      htmlFor="company-name"
                      className="block text-xs font-mono font-semibold uppercase tracking-wider text-ink-secondary"
                    >
                      Company Name *
                    </label>
                    <input
                      id="company-name"
                      type="text"
                      required
                      value={company}
                      onChange={(e) => setCompany(e.target.value)}
                      placeholder="e.g. Apex Distribution Ltd."
                      className="w-full px-4 py-2.5 rounded-xl border border-charcoal/20 bg-white text-sm text-ink placeholder:text-ink-muted focus:outline-none focus:ring-2 focus:ring-plum focus:border-plum"
                    />
                  </div>
                </div>

                {/* Message */}
                <div className="space-y-1.5">
                  <label
                    htmlFor="requirements-message"
                    className="block text-xs font-mono font-semibold uppercase tracking-wider text-ink-secondary"
                  >
                    Requirements or Questions
                  </label>
                  <textarea
                    id="requirements-message"
                    rows={4}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Tell us about your distribution tiers, field team size, or operational goals..."
                    className="w-full px-4 py-2.5 rounded-xl border border-charcoal/20 bg-white text-sm text-ink placeholder:text-ink-muted focus:outline-none focus:ring-2 focus:ring-plum focus:border-plum resize-none"
                  />
                </div>

                {/* Submit Button */}
                <div className="pt-2">
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full text-base font-semibold text-white bg-plum hover:bg-plum-hover transition-colors shadow-md disabled:opacity-70 focus:outline-none focus-visible:ring-2 focus-visible:ring-plum"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Submitting Inquiry...
                      </>
                    ) : (
                      <>
                        Submit Enterprise Inquiry
                        <Send className="w-4 h-4" />
                      </>
                    )}
                  </button>
                  <p className="text-center text-xs text-ink-muted mt-3">
                    By submitting, you agree to our{" "}
                    <a
                      href="/privacy-policy"
                      className="underline hover:text-ink"
                    >
                      Privacy Policy
                    </a>
                    . Strict confidentiality guaranteed.
                  </p>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
